import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import {
  type UnitRosterMember,
  membersFromReport,
} from '@modules/roster-lite/models/unitRosterMember';
import { useGetUnitRosterLiteReportQuery } from '@modules/rtk/webscript-api';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';

interface UseUnitRosterResult {
  members: UnitRosterMember[];
  isFetching: boolean;
  isError: boolean;
  /** Server-provided `response_message` when the report errored, if any. */
  errorMessage?: string;
  error: unknown;
}

/**
 * Pulls the report's `response_message` out of an RTK error. A `response_code`
 * of `-1` fails the webscript base query's `validateStatus`, so the parsed body
 * (with the human-readable message) lands on `error.data`.
 */
function getReportErrorMessage(error: unknown): string | undefined {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object' &&
    'response_message' in error.data
  ) {
    const message = (error.data as { response_message?: unknown })
      .response_message;
    return typeof message === 'string' && message ? message : undefined;
  }
  return undefined;
}

/**
 * Fetches the `SBLUnitRosterLiteReport` for an org and maps it into the domain
 * model. Defaults to `organizationGuidSel` (strips composite parent/guardian
 * guids so the report request doesn't 403).
 * THIS IS MEAN TO BE USED ONLY FOR PARENT/YOUTH
 */
export function useUnitRoster(orgGuid?: string): UseUnitRosterResult {
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const organizationGuid = orgGuid ?? selectedOrgGuid;

  const { data, isFetching, isError, error } = useGetUnitRosterLiteReportQuery(
    { organizationGuid },
    { skip: !organizationGuid },
  );

  const members = useMemo(() => (data ? membersFromReport(data) : []), [data]);

  return {
    members,
    isFetching,
    isError,
    errorMessage: getReportErrorMessage(error),
    error,
  };
}
